import {useMemo} from "react";
import {BookingPostKind, ScheduledPaymentType, useGetBookingByIdQuery} from "../generated/graphql";
import {options} from "../views/PanelCardPayments/AddNamedScheduledPaymentForm";

/**
 * Trennung „Kursgebühr“ vs. „Zusatzposten/Gebühr“ im Buchungseditor.
 *
 * Die Art kommt vom Server (`ScheduledPayment.postKind`, dieselbe Regel wie beim Umbuchen).
 * Vorschau-Zeilen kennen das Feld nicht, behalten aber die Id gespeicherter Posten — daher
 * Nachschlagen per Id in der geladenen Buchung. Zeilen ohne Treffer (neue Buchung, im Formular
 * umgetypte Rate) folgen der Typ-Regel des Servers: AdditionalItem → Zusatzposten, Mahn-/Storno-/
 * Rücklastschrift-Typ → Gebühr, sonst Kursgebühr (Marker kennt der Client nicht).
 *
 * Posten mit `carriedOverAt` stammen aus der bisherigen Buchung einer Umbuchung (Stichtag-Regel
 * oder übernommene Posten) und bekommen einen eigenen Block — im Block „Zusatzposten und
 * Gebühren“ wirkte die verrechnete Lektion des alten Kurses wie ein Aufschlag (aqua-vision, 26.10.2026).
 */
export const CARRIED_OVER = "CarriedOver";
export type ClientPostKind = BookingPostKind | typeof CARRIED_OVER;
export type PostKindById = Map<string, ClientPostKind>;

type RateLike = {id?: string | null; type?: ScheduledPaymentType | null; title?: string | null};

export const postKindById = (rates?: Array<{id?: string | null; postKind?: BookingPostKind | null; carriedOverAt?: string | Date | null}> | null): PostKindById =>
    new Map((rates ?? []).filter((rate) => rate.id && rate.postKind).map((rate) => [rate.id as string, rate.carriedOverAt ? CARRIED_OVER : (rate.postKind as BookingPostKind)]));

const FEE_TYPES: Array<ScheduledPaymentType> = [
    ScheduledPaymentType.DunningLevel1,
    ScheduledPaymentType.DunningLevel2,
    ScheduledPaymentType.DunningLevel3,
    ScheduledPaymentType.CancelationFee,
    ScheduledPaymentType.Chargeback,
];

const postKindByType = (type?: ScheduledPaymentType | null): BookingPostKind =>
    type === ScheduledPaymentType.AdditionalItem ? BookingPostKind.AdditionalItem : type && FEE_TYPES.includes(type) ? BookingPostKind.Fee : BookingPostKind.CourseFee;

export const postKindOf = (rate: RateLike, kindById: PostKindById): ClientPostKind => (rate.id ? kindById.get(rate.id) : undefined) ?? postKindByType(rate.type);

/** Kursraten behalten ihren Original-Index (Formik-Callbacks arbeiten mit Indizes), Posten aus der bisherigen Buchung und sonstige Posten kommen in je einen eigenen Block. */
export const splitByPostKind = <T extends RateLike>(rates: Array<T>, kindById: PostKindById): {courseFees: Array<{rate: T; index: number}>; carriedOver: Array<{rate: T; index: number}>; posts: Array<{rate: T; index: number}>} => {
    const entries = rates.map((rate, index) => ({rate, index, kind: postKindOf(rate, kindById)}));

    return {
        courseFees: entries.filter(({kind}) => kind === BookingPostKind.CourseFee),
        carriedOver: entries.filter(({kind}) => kind === CARRIED_OVER),
        posts: entries.filter(({kind}) => kind !== BookingPostKind.CourseFee && kind !== CARRIED_OVER),
    };
};

/** Bezeichnung einer Zeile: eigener Titel, sonst das Label des Typs („Kursgebühr“, „Mahnstufe 1“ …). */
export const rateLabel = (rate: RateLike): string => {
    const title = rate.title?.trim();

    if (title) return title;

    return options.find((option) => option.type === (rate.type ?? ScheduledPaymentType.Standard))?.label ?? "Kursgebühr";
};

export const postKindLabel: Record<BookingPostKind, string> = {
    [BookingPostKind.CourseFee]: "Kursgebühr",
    [BookingPostKind.Fee]: "Gebühr",
    [BookingPostKind.AdditionalItem]: "Zusatzposten",
};

/** Zweite Zeile eines Postens: Art nach Server, bei übernommenen Zeilen die Art nach Typ (Gebühr, Zusatzposten, Kursgebühr des alten Kurses) — der Block nennt die Herkunft schon. */
export const postRowLabel = (rate: RateLike, kind: ClientPostKind): string => postKindLabel[kind === CARRIED_OVER ? postKindByType(rate.type) : kind];

/** Standard-Editor: die Buchung liegt bereits im Apollo-Cache (BookingCreate lädt dieselbe Query). */
export const usePostKindById = (bookingId?: string | null): PostKindById => {
    const {data} = useGetBookingByIdQuery({variables: {id: bookingId ?? ""}, skip: !bookingId, fetchPolicy: "cache-first"});

    return useMemo(() => postKindById(data?.bookingById?.scheduledPayments), [data]);
};
